// Service detail (§11 `/services/:id`): tasks, signing keys, maintenance windows, flow-control
// caps, quotas. Read-only summary plus the service's tasks, with inline edit (PATCH + If-Match
// optimistic concurrency — 412 surfaces as a conflict banner) and soft-delete.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Api, ApiError, type Service, type SigningKey } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { fmtTime } from "../lib/format";
import { Badge, Card, Empty, ErrorBox, Field, Spinner, StatusBadge } from "../components/ui";

// A signing-key row in the editor. `_secret`/`_envName`/`_stored` are transient, client-only fields
// used when revealing a freshly generated secret once; none are sent in the PATCH body (Cadence
// never persists raw secrets — only the secretName reference). `_stored` tracks whether the secret
// was already written to Secrets Store via auto-provisioning (drives button state).
type KeyRow = SigningKey & { _secret?: string; _envName?: string; _stored?: boolean };

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* clipboard may be unavailable (insecure context) — user can select manually */
  }
}

/** Suggest a target-app env var name from a kid, e.g. "k1" -> "CADENCE_SIGNING_KEY_K1". */
function defaultEnvName(kid: string): string {
  const slug = (kid || "key").toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return `CADENCE_SIGNING_KEY_${slug || "KEY"}`;
}

export function ServiceDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const svc = useAsync(() => Api.getService(id), [id]);
  const tasks = useAsync(() => Api.listTasks({ serviceId: id }), [id]);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionErr, setActionErr] = useState<Error | null>(null);

  if (svc.loading && !svc.data) return <Spinner />;
  if (svc.error) return <ErrorBox error={svc.error} />;
  if (!svc.data) return null;
  const s = svc.data;

  async function onDelete() {
    if (!confirm(`Soft-delete service "${s.id}"? Its tasks will stop scheduling.`)) return;
    setActionErr(null);
    setDeleting(true);
    try {
      await Api.deleteService(s.id, s.version);
      navigate("/services");
    } catch (e) {
      setActionErr(toError(e));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-mono text-xl font-semibold text-white">{s.id}</h1>
          <div className="text-sm text-muted">{s.name}</div>
        </div>
        <div className="flex gap-2">
          {!editing && (
            <button className="btn" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
          <button className="btn btn-danger" onClick={onDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </button>
          <Link className="btn" to="/services">
            Back
          </Link>
        </div>
      </div>

      {actionErr && <ErrorBox error={actionErr} />}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {editing ? (
          <EditServiceCard
            service={s}
            onCancel={() => setEditing(false)}
            onSaved={() => {
              setEditing(false);
              svc.reload();
            }}
          />
        ) : (
          <Card title="Configuration">
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <Dt>Base URL</Dt>
              <dd className="mono text-muted">{s.baseUrl}</dd>
              <Dt>Signing mode</Dt>
              <dd>
                <Badge tone={s.signingMode === "required" ? "ok" : "muted"}>{s.signingMode}</Badge>
              </dd>
              <Dt>Default timeout</Dt>
              <dd className="mono">{s.defaultTimeoutMs} ms</dd>
              <Dt>Max RPS</Dt>
              <dd className="mono">{s.maxRps ?? "—"}</dd>
              <Dt>Max parallelism</Dt>
              <dd className="mono">{s.maxParallelism ?? "—"}</dd>
              <Dt>Max tasks</Dt>
              <dd className="mono">{s.maxTasks ?? "—"}</dd>
              <Dt>Retain days</Dt>
              <dd className="mono">{s.retainDays}</dd>
              <Dt>Enabled</Dt>
              <dd>{s.enabled ? <Badge tone="ok">enabled</Badge> : <Badge tone="danger">disabled</Badge>}</dd>
              <Dt>Version</Dt>
              <dd className="mono">{s.version}</dd>
            </dl>
          </Card>
        )}

        <Card title="Signing keys">
          {!s.signingKeys || s.signingKeys.length === 0 ? (
            <Empty>No signing keys.</Empty>
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="th">kid</th>
                  <th className="th">secret ref</th>
                  <th className="th">active</th>
                  <th className="th">created</th>
                </tr>
              </thead>
              <tbody>
                {s.signingKeys.map((k) => (
                  <tr key={k.kid}>
                    <td className="td mono">{k.kid}</td>
                    <td className="td mono text-muted">{k.secretName}</td>
                    <td className="td">{k.active ? <Badge tone="ok">active</Badge> : <Badge>inactive</Badge>}</td>
                    <td className="td mono text-xs">{fmtTime(k.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <Card
        title="Tasks"
        actions={
          <Link className="btn btn-primary" to={`/tasks/new?serviceId=${s.id}`}>
            New task
          </Link>
        }
      >
        {tasks.loading && !tasks.data ? (
          <Spinner />
        ) : tasks.error ? (
          <ErrorBox error={tasks.error} />
        ) : !tasks.data || tasks.data.data.length === 0 ? (
          <Empty>No tasks for this service.</Empty>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="th">ID</th>
                <th className="th">Schedule</th>
                <th className="th">Engine</th>
                <th className="th">Next run</th>
                <th className="th">State</th>
              </tr>
            </thead>
            <tbody>
              {tasks.data.data.map((t) => (
                <tr key={t.id}>
                  <td className="td">
                    <Link className="mono text-primary hover:underline" to={`/tasks/${t.id}`}>
                      {t.id}
                    </Link>
                  </td>
                  <td className="td mono text-xs">
                    {t.scheduleKind === "cron"
                      ? t.cron
                      : t.scheduleKind === "interval"
                        ? `every ${t.intervalSeconds}s`
                        : "one-shot"}
                  </td>
                  <td className="td">
                    <Badge tone={t.scheduleEngine === "do_alarm" ? "warn" : "muted"}>{t.scheduleEngine}</Badge>
                  </td>
                  <td className="td mono text-xs">{fmtTime(t.nextRunAt)}</td>
                  <td className="td">
                    {t.deletedAt ? (
                      <StatusBadge status="deleted" />
                    ) : t.paused ? (
                      <Badge tone="warn">paused</Badge>
                    ) : t.enabled ? (
                      <Badge tone="ok">enabled</Badge>
                    ) : (
                      <Badge tone="danger">disabled</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function EditServiceCard({
  service,
  onCancel,
  onSaved,
}: {
  service: Service;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(service.name);
  const [baseUrl, setBaseUrl] = useState(service.baseUrl);
  const [signingMode, setSigningMode] = useState<"required" | "none">(service.signingMode);
  const [defaultTimeoutMs, setDefaultTimeoutMs] = useState(String(service.defaultTimeoutMs));
  const [maxRps, setMaxRps] = useState(service.maxRps != null ? String(service.maxRps) : "");
  const [maxParallelism, setMaxParallelism] = useState(
    service.maxParallelism != null ? String(service.maxParallelism) : "",
  );
  const [maxTasks, setMaxTasks] = useState(service.maxTasks != null ? String(service.maxTasks) : "");
  const [retainDays, setRetainDays] = useState(String(service.retainDays));
  const [enabled, setEnabled] = useState(service.enabled);
  // `_secret` is a transient, client-only field holding a freshly generated secret to reveal once.
  // It is NEVER sent to the server (cleanKeys strips it) — Cadence persists only the reference name.
  const [keys, setKeys] = useState<KeyRow[]>(service.signingKeys ?? []);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  // Whether the deployment supports auto-provisioning (admin worker has the Secrets Store binding +
  // config). Drives the "Save to Secrets Store" button vs. the manual CLI fallback.
  const secretsCfg = useAsync(() => Api.secretsConfig(), []);
  // Per-row provisioning state (keyed by row index): "saving" | "saved" | error message.
  const [provision, setProvision] = useState<Record<number, string>>({});

  function num(v: string): number | null {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }

  function nextKid(existing: KeyRow[]): string {
    const used = new Set(existing.map((k) => k.kid.trim()).filter(Boolean));
    for (let i = 1; i < 1000; i++) {
      const candidate = `k${i}`;
      if (!used.has(candidate)) return candidate;
    }
    return `k${Date.now()}`;
  }

  function addKey() {
    setKeys((ks) => [...ks, { kid: nextKid(ks), secretName: "", active: true, createdAt: Date.now() }]);
  }
  function updateKey(idx: number, patch: Partial<KeyRow>) {
    setKeys((ks) => ks.map((k, i) => (i === idx ? { ...k, ...patch } : k)));
  }
  function removeKey(idx: number) {
    setKeys((ks) => ks.filter((_, i) => i !== idx));
  }

  /** Generate a cryptographically-secure 256-bit secret in-browser and reveal it once. */
  function generateSecret(idx: number) {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    setKeys((ks) =>
      ks.map((k, i) => {
        if (i !== idx) return k;
        const kid = k.kid.trim() || nextKid(ks);
        const secretName = k.secretName.trim() || `${service.id}-sign-${kid}`;
        return { ...k, kid, secretName, active: true, _secret: hex };
      }),
    );
  }

  function dismissSecret(idx: number) {
    setKeys((ks) => ks.map((k, i) => (i === idx ? { ...k, _secret: undefined } : k)));
  }

  /** Auto-provision: write this row's freshly generated secret straight into Secrets Store. */
  async function saveToSecretsStore(idx: number) {
    const k = keys[idx];
    if (!k || !k._secret) return;
    const kid = k.kid.trim() || nextKid(keys);
    const secretName = k.secretName.trim() || `${service.id}-sign-${kid}`;
    setProvision((p) => ({ ...p, [idx]: "saving" }));
    try {
      await Api.provisionSecret({ serviceId: service.id, kid, secretName, secret: k._secret });
      setProvision((p) => ({ ...p, [idx]: "saved" }));
      setKeys((ks) => ks.map((row, i) => (i === idx ? { ...row, kid, secretName, _stored: true } : row)));
    } catch (e) {
      setProvision((p) => ({ ...p, [idx]: toError(e).message }));
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) return setErr(new Error("Name is required"));
    if (!baseUrl.startsWith("https://") || baseUrl.length < 9) {
      return setErr(new Error("Base URL must be an absolute https:// URL"));
    }

    // Normalize signing keys: trim, drop blank rows, reject incomplete/duplicate kids.
    // `createdAt` is part of the schema contract — preserve existing, default to now for new keys.
    // Transient client-only fields (_secret/_envName) are intentionally NOT included.
    const cleanKeys = keys
      .map((k) => ({
        kid: k.kid.trim(),
        secretName: k.secretName.trim(),
        active: k.active,
        createdAt: typeof k.createdAt === "number" ? k.createdAt : Date.now(),
      }))
      .filter((k) => k.kid !== "" || k.secretName !== "");
    for (const k of cleanKeys) {
      if (!k.kid) return setErr(new Error("Every signing key needs a kid (key id)."));
      if (!k.secretName) return setErr(new Error(`Signing key "${k.kid}" needs a Secrets Store reference name.`));
    }
    const kids = cleanKeys.map((k) => k.kid);
    if (new Set(kids).size !== kids.length) return setErr(new Error("Signing key ids (kid) must be unique."));
    if (signingMode === "required" && !cleanKeys.some((k) => k.active)) {
      return setErr(new Error("Signing mode 'required' needs at least one active signing key."));
    }

    // Partial update — only send fields the form controls. null clears an optional cap.
    const body: Record<string, unknown> = {
      name: name.trim(),
      baseUrl: baseUrl.trim(),
      signingMode,
      defaultTimeoutMs: num(defaultTimeoutMs) ?? service.defaultTimeoutMs,
      retainDays: num(retainDays) ?? service.retainDays,
      enabled,
      maxRps: num(maxRps),
      maxParallelism: num(maxParallelism),
      maxTasks: num(maxTasks),
      signingKeys: cleanKeys.length > 0 ? cleanKeys : null,
    };

    setSubmitting(true);
    try {
      await Api.updateService(service.id, body, service.version);
      onSaved();
    } catch (e2) {
      if (e2 instanceof ApiError && e2.isConflict) {
        setErr(new Error("This service changed since you opened it. Close and reopen to get the latest version."));
      } else {
        setErr(toError(e2));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card title={`Edit configuration (v${service.version})`}>
      <form onSubmit={submit}>
        <Field label="Name">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Base URL" hint="Absolute https:// origin. Task paths are appended to this.">
          <input className="input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        </Field>
        <Field label="Signing mode">
          <select className="input" value={signingMode} onChange={(e) => setSigningMode(e.target.value as "required" | "none")}>
            <option value="none">none</option>
            <option value="required">required</option>
          </select>
        </Field>
        <Field label="Default timeout (ms)">
          <input className="input" value={defaultTimeoutMs} onChange={(e) => setDefaultTimeoutMs(e.target.value)} inputMode="numeric" />
        </Field>
        <Field label="Max RPS" hint="Blank = unlimited.">
          <input className="input" value={maxRps} onChange={(e) => setMaxRps(e.target.value)} inputMode="numeric" placeholder="—" />
        </Field>
        <Field label="Max parallelism" hint="Blank = unlimited.">
          <input className="input" value={maxParallelism} onChange={(e) => setMaxParallelism(e.target.value)} inputMode="numeric" placeholder="—" />
        </Field>
        <Field label="Max tasks" hint="Blank = unlimited.">
          <input className="input" value={maxTasks} onChange={(e) => setMaxTasks(e.target.value)} inputMode="numeric" placeholder="—" />
        </Field>
        <Field label="Retain days">
          <input className="input" value={retainDays} onChange={(e) => setRetainDays(e.target.value)} inputMode="numeric" />
        </Field>
        <Field label="Enabled">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className="text-muted">Service accepts scheduling</span>
          </label>
        </Field>

        <div className="mb-4">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-sm font-medium text-white">Signing keys</span>
            <button type="button" className="btn btn-sm" onClick={addKey}>
              + Add key
            </button>
          </div>
          <p className="mb-2 text-xs text-muted">
            HMAC-SHA256 shared secrets. Click <span className="mono">Generate</span> to create a secure 256-bit
            secret in your browser — it is shown <strong>once</strong> and never sent to or stored by Cadence
            (only the <span className="mono">secretName</span> reference is saved). Copy it now into both
            Cloudflare Secrets Store and your target app. Keep an old key active during rotation, then
            deactivate it.
          </p>
          {keys.length === 0 ? (
            <div className="rounded border border-dashed border-white/10 px-3 py-2 text-xs text-muted">
              No signing keys. {signingMode === "required" && "Required signing needs at least one active key."}
            </div>
          ) : (
            <div className="space-y-3">
              {keys.map((k, i) => (
                <div key={i} className="rounded border border-border p-2">
                  <div className="flex items-center gap-2">
                    <input
                      className="input flex-1"
                      placeholder="kid (e.g. k1)"
                      value={k.kid}
                      onChange={(e) => updateKey(i, { kid: e.target.value })}
                    />
                    <input
                      className="input flex-[2]"
                      placeholder="secret ref (Secrets Store name)"
                      value={k.secretName}
                      onChange={(e) => updateKey(i, { secretName: e.target.value })}
                    />
                    <label className="flex items-center gap-1 text-xs text-muted">
                      <input
                        type="checkbox"
                        checked={k.active}
                        onChange={(e) => updateKey(i, { active: e.target.checked })}
                      />
                      active
                    </label>
                    <button type="button" className="btn btn-sm" onClick={() => generateSecret(i)}>
                      Generate
                    </button>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => removeKey(i)}>
                      Remove
                    </button>
                  </div>

                  {k._secret && (
                    <div className="mt-2 rounded border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
                      <div className="mb-2 font-semibold text-amber-300">
                        Secret for "{k.kid}" — shown once. Copy it now; it cannot be retrieved later.
                      </div>

                      <div className="mb-1 text-muted">
                        Target app env var (one-paste into Vercel / <span className="mono">.env</span>):
                      </div>
                      <div className="mb-1 flex items-center gap-2">
                        <input
                          className="input flex-1"
                          value={k._envName ?? defaultEnvName(k.kid)}
                          onChange={(e) => updateKey(i, { _envName: e.target.value })}
                          spellCheck={false}
                        />
                        <span className="text-muted">name</span>
                      </div>
                      <div className="mb-2 flex items-center gap-2">
                        <code className="mono flex-1 break-all rounded bg-black/40 px-2 py-1 text-amber-100">
                          {`${k._envName ?? defaultEnvName(k.kid)}="${k._secret}"`}
                        </code>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => copyText(`${k._envName ?? defaultEnvName(k.kid)}="${k._secret}"`)}
                        >
                          Copy env var
                        </button>
                      </div>

                      <div className="mb-1 text-muted">Raw secret only:</div>
                      <div className="mb-2 flex items-center gap-2">
                        <code className="mono flex-1 break-all rounded bg-black/40 px-2 py-1 text-slate-200">
                          {k._secret}
                        </code>
                        <button type="button" className="btn btn-sm" onClick={() => copyText(k._secret!)}>
                          Copy secret
                        </button>
                      </div>

                      <div className="mb-1 text-muted">Store in Cloudflare Secrets Store:</div>
                      {secretsCfg.data?.configured ? (
                        <div className="mb-2">
                          <div className="mb-1 flex items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={provision[i] === "saving" || provision[i] === "saved"}
                              onClick={() => saveToSecretsStore(i)}
                            >
                              {provision[i] === "saving"
                                ? "Saving…"
                                : provision[i] === "saved"
                                  ? "✓ Saved to Secrets Store"
                                  : "Save to Secrets Store"}
                            </button>
                            <span className="text-muted">
                              writes <span className="mono">{k.secretName}</span> to store{" "}
                              <span className="mono">{secretsCfg.data.storeId}</span>
                            </span>
                          </div>
                          {provision[i] && provision[i] !== "saving" && provision[i] !== "saved" && (
                            <div className="text-xs text-rose-400">{provision[i]}</div>
                          )}
                          <details className="mt-1">
                            <summary className="cursor-pointer text-xs text-muted">
                              Prefer the CLI? Show manual command
                            </summary>
                            <div className="mt-1 flex items-center gap-2">
                              <code className="mono flex-1 break-all rounded bg-black/40 px-2 py-1 text-slate-200">
                                {`wrangler secrets-store secret create ${secretsCfg.data.storeId ?? "<STORE_ID>"} --name ${k.secretName} --scopes workers --value ${k._secret}`}
                              </code>
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() =>
                                  copyText(
                                    `wrangler secrets-store secret create ${secretsCfg.data?.storeId ?? "<STORE_ID>"} --name ${k.secretName} --scopes workers --value ${k._secret}`,
                                  )
                                }
                              >
                                Copy cmd
                              </button>
                            </div>
                          </details>
                        </div>
                      ) : (
                        <div className="mb-2 flex items-center gap-2">
                          <code className="mono flex-1 break-all rounded bg-black/40 px-2 py-1 text-slate-200">
                            {`wrangler secrets-store secret create <STORE_ID> --name ${k.secretName} --scopes workers --value ${k._secret}`}
                          </code>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() =>
                              copyText(
                                `wrangler secrets-store secret create <STORE_ID> --name ${k.secretName} --scopes workers --value ${k._secret}`,
                              )
                            }
                          >
                            Copy cmd
                          </button>
                        </div>
                      )}

                      <button type="button" className="btn btn-sm" onClick={() => dismissSecret(i)}>
                        I've copied it — hide
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {err && (
          <div className="mb-3">
            <ErrorBox error={err} />
          </div>
        )}

        <div className="flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Saving…" : "Save changes"}
          </button>
          <button type="button" className="btn" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}

function toError(e: unknown): Error {
  if (e instanceof ApiError) {
    return new Error(`${e.message}${e.details ? ` — ${JSON.stringify(e.details)}` : ""}`);
  }
  return e instanceof Error ? e : new Error(String(e));
}

function Dt({ children }: { children: React.ReactNode }) {
  return <dt className="text-muted">{children}</dt>;
}